// Type: Service

// Create auth service class

import API from "../../helpers/network/http";
import {User} from "../../utils/types";
import {getLS} from "../../helpers/ls";

const normalizeEmail = (value: string | null | undefined) => value?.trim().toLowerCase();
const normalizeUsername = (value: string | null | undefined) => value?.trim().toLowerCase();

export default class AuthService {
    // Create a method to register a user
    static async register(user: Partial<User>) {
        // Prepare the request body
        const payload: Record<string, any> = {
            email: normalizeEmail(user.email),
            password: user.password,
        }
        if (user.first_name) payload.first_name = user.first_name;
        if (user.last_name) payload.last_name = user.last_name;
        if (user.username) payload.username = normalizeUsername(user.username);
        if (user.phone_number) payload.phone_number = user.phone_number;
        if (user.default_part) payload.default_part = user.default_part;
        if (user.referral_code) payload.referral_code = user.referral_code;
        if (user.signup_attribution) payload.signup_attribution = user.signup_attribution;
        // Call the register endpoint from the API
        const {data} = await API.post("/users", payload);

        // Return the response
        return data;
    }

    // Create a method to register a user with google
    static async registerWithGoogle(token: string, referral_code: string | undefined, signup_attribution?: Record<string, string>) {
        // Prepare the request body
        let payload: {
            token: string;
            referral_code?: string;
            signup_attribution?: Record<string, string>;
        } = {
            token
        }
        if (signup_attribution) payload.signup_attribution = signup_attribution;
        // Check if referral code is present
        if (referral_code) {
            payload = {
                ...payload,
                referral_code
            }
        }
        // Call the register endpoint from the API
        const {data} = await API.post("/users/google", payload);

        // Return the response
        return data;
    }

    // Create a method to login a user
    static async login(user: Partial<User>) {
        // Prepare the request body
        const payload = {
            email: normalizeEmail(user.email),
            password: user.password
        }
        // Call the login endpoint from the API
        const {data} = await API.post("/auth/login", payload);


        // Return the response
        return data;
    }

    static async doesEmailExist(email: string): Promise<boolean> {
        try {
            await API.get(`/users/email/${encodeURIComponent(normalizeEmail(email) || '')}/check?checking=true`);
            return true;
        } catch (error: any) {
            if (error?.response?.status === 404) return false;
            throw error;
        }
    }

    static async isUsernameAvailable(username: string): Promise<boolean> {
        try {
            const {data} = await API.get(`/users/username/${encodeURIComponent(normalizeUsername(username) || '')}/check?checking=true`);
            return Boolean(data?.data?.available);
        } catch (error: any) {
            if (error?.response?.status === 409) return false;
            throw error;
        }
    }

    // Create a method to confirm a user
    static async confirm(email: string, otp: string) {
        const payload = {
            email: normalizeEmail(email),
            otp
        }
        // Call the login endpoint from the API
        const {data} = await API.post("/auth/login/verify", payload);


        // Return the response
        return data;
    }

    // Create a method to login a user with Google
    static async loginWithGoogle(token: string, referral_code: string | undefined, signup_attribution?: Record<string, string>) {
        // Prepare the request body
        let payload: {
            token: string;
            referral_code?: string;
            signup_attribution?: Record<string, string>;
        } = {
            token,
        }
        if (signup_attribution) payload.signup_attribution = signup_attribution;

        // Check if referral code is present
        if (referral_code) {
            payload = {
                ...payload,
                referral_code
            }
        }
        // Call the login endpoint from the API
        const {data} = await API.post("/auth/login/google", payload);


        // Return the response
        return data;
    }

    // Create a method to login a user with Google
    static async loginWithApple(_data: any) {
        // Prepare the request body
        const payload = {
            token: _data.idToken,
            family_name: _data.familyName,
            given_name: _data.givenName
        }
        // Call the login endpoint from the API
        const {data} = await API.post("/auth/login/apple", payload);


        // Return the response
        return data;
    }

    static async registerWithApple(_data: any) {
        const payload = {
            token: _data.idToken,
            email: _data.email,
            family_name: _data.familyName,
            given_name: _data.givenName,
            authorization_code: _data.authorizationCode,
            referral_code: _data.referral_code,
            signup_attribution: _data.signup_attribution,
        };
        const {data} = await API.post('/users/apple', payload);
        return data;
    }

    // Create a method to fetch a user
    static async fetchUser() {
        // Call the login endpoint from the API
        const {data} = await API.get("/auth/me");


        // Return the response
        return data;
    }

    // Forgot Password
    static async forgotPassword(email: string) {
        const payload = {
            email: normalizeEmail(email)
        }
        // Call the login endpoint from the API
        const {data} = await API.post("/auth/password-reset", payload);

        // Return the response
        return data;
    }

    // Reset Password
    static async resetPassword(email: string, otp: string, password: string) {
        const payload = {
            email: normalizeEmail(email),
            otp,
            password
        }
        // Call the login endpoint from the API
        const {data} = await API.post("/auth/password-reset/verify", payload);

        // Return the response
        return data;
    }

    // Logout user
    static async logout(token: string | null) {
        // Call the logout endpoint from the API
        const {data} = await API.post("/auth/logout", {
            refresh_token: token || getLS('refresh_token')
        })

        // Return the response
        return data
    }

    // Update user
    static async updateUser(user: Partial<User>) {
        // Prepare the request body
        const payload: Record<string, any> = {};
        if (user.username !== undefined) payload.username = normalizeUsername(user.username);
        if (user.first_name !== undefined) payload.first_name = user.first_name;
        if (user.last_name !== undefined) payload.last_name = user.last_name;
        if (user.phone_number !== undefined) payload.phone_number = user.phone_number;
        if (user.default_part !== undefined) payload.default_part = user.default_part;
        if (user.avatar !== undefined) payload.avatar = user.avatar;
        if (user.profile_banner !== undefined) payload.profile_banner = user.profile_banner;
        if (user.country !== undefined) payload.country = user.country;
        // Call the register endpoint from the API
        const {data} = await API.put("/users", payload);

        // Return the response
        return data;
    }
}
